import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import { Text, Box, Flex } from 'components/ui';
import {
    space, radii, colors, fonts, gradients
} from 'style/config';
import store from 'store/Store';
import SuperChatActions from 'ai/actions/superChatActions';
import { ENTRYPOINTS } from 'ai/constants/superchat';
import Flag from 'components/Flag/Flag';
import Location from 'utils/Location';
import { getCategoryLabelKey } from 'ai/utils/aiBeautyChat';

function AIBeautyChatContent(props) {
    const {
        localization, page, featuresAIBeautyChat, contextPrompts, handlePromptClick
    } = props;

    const handleStartChat = () => {
        const isSuperChatOpen = store.getState()?.superChat?.showSuperChat;

        if (!isSuperChatOpen) {
            store.dispatch(SuperChatActions.showSuperChat(true, [], { entrypoint: ENTRYPOINTS.DEFAULT, source: 'header' }));
        }
    };

    const onPromptClick = promptContent => {
        if (handlePromptClick && page && featuresAIBeautyChat) {
            handlePromptClick(contextPrompts, page, featuresAIBeautyChat, promptContent);
        }
    };

    // Use contextual prompts from Redux store
    const suggestedPrompts = contextPrompts || [];

    // Determine the label to display based on page context
    const isHomepage = Location.isHomepage();
    const categoryLabelKey = !isHomepage ? getCategoryLabelKey(page) : null;
    const displayLabel = isHomepage ? localization?.tryAsking : categoryLabelKey ? localization?.[categoryLabelKey] : localization?.tryAsking;

    return (
        <Box
            display='flex'
            flexDirection='column'
            minWidth='374px'
        >
            {/* Top Section */}
            <Box
                display='flex'
                flexDirection='column'
                gap={`${space[3]}px`}
                padding={`0 ${space[4]}px`}
            >
                {/* Description Box */}
                <Box
                    backgroundColor='rgba(255, 255, 255, 0.2)'
                    css={styles.descriptionBox}
                >
                    <Text
                        fontSize='base'
                        lineHeight='base'
                        fontFamily={fonts.base}
                    >
                        {localization?.description}
                    </Text>
                </Box>

                {/* Start Chat CTA */}
                <button
                    type='button'
                    onClick={handleStartChat}
                    css={styles.startChatButton}
                >
                    <Text
                        fontSize='base'
                        fontWeight='bold'
                        lineHeight='base'
                        textAlign='center'
                        color={colors.black}
                        fontFamily={fonts.base}
                    >
                        {localization?.startChat}
                    </Text>
                </button>
            </Box>

            {/* Gray Spacer */}
            <Box
                height='8px'
                backgroundColor='nearWhite'
                width='100%'
                marginTop={space[4]}
            />

            {/* Bottom Section - Try Asking */}
            <Box
                display='flex'
                flexDirection='column'
                gap={`${space[2]}px`}
                padding={`0 ${space[4]}px`}
            >
                <Text
                    fontSize='md'
                    fontWeight='bold'
                    lineHeight='tight'
                    css={styles.tryAskingHeading}
                >
                    {displayLabel}
                </Text>
                {suggestedPrompts.length > 0
                    ? suggestedPrompts.map((prompt, index) => {
                        const promptText = prompt?.content || prompt;
                        const hasNewFlag = prompt?.is_new || false;

                        return (
                            <button
                                key={index}
                                type='button'
                                onClick={() => onPromptClick(promptText)}
                                css={styles.promptItem}
                            >
                                <Flex
                                    alignItems='center'
                                    gap={space[1]}
                                    width='100%'
                                >
                                    <Text
                                        fontSize='base'
                                        lineHeight='base'
                                        fontFamily={fonts.base}
                                        css={styles.promptText}
                                    >
                                        {promptText}
                                    </Text>
                                    {hasNewFlag && (
                                        <Flag
                                            backgroundColor='black'
                                            fontSize={10}
                                            paddingX={space[1]}
                                            paddingY={0}
                                            css={styles.newFlag}
                                        >
                                            {localization?.new}
                                        </Flag>
                                    )}
                                </Flex>
                            </button>
                        );
                    })
                    : null}
            </Box>
        </Box>
    );
}

const styles = {
    descriptionBox: {
        flex: '0 0 auto',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '10px',
        width: '100%'
    },
    startChatButton: {
        display: 'flex',
        height: '44px',
        padding: '12px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        alignSelf: 'stretch',
        border: 'none',
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
        width: '100%',
        borderRadius: radii.full,
        backgroundColor: 'transparent',
        backgroundImage: gradients.aiBeautyChat,
        textDecoration: 'none',
        margin: 0,
        '&:hover': {
            opacity: 0.9
        },
        '&:active': {
            opacity: 0.8
        }
    },
    tryAskingHeading: {
        marginTop: space[3],
        marginBottom: space[2]
    },
    newFlag: {
        height: '13px',
        lineHeight: '13px',
        paddingTop: 0,
        paddingBottom: 0,
        textTransform: 'lowercase',
        '&::first-letter': {
            textTransform: 'uppercase'
        }
    },
    promptItem: {
        cursor: 'pointer',
        padding: `${space[1]}px 0`,
        transition: 'opacity 0.2s ease',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        width: '100%',
        '&:hover': {
            opacity: 0.7
        }
    },
    promptText: {
        flex: 1
    }
};

export default wrapFunctionalComponent(AIBeautyChatContent, 'AIBeautyChatContent');
